import { useContext, useEffect, useState } from 'react';
import UserContext from 'components/context/UserContext';
import { useQuery, useMutation } from '@apollo/client';
import CompanySettingCategory, { CompanySettingkey } from 'utils/enum/CompanySettingEnum';
import CompanyQuery from 'services/graphQL/query/setting/CompanyQuery';
import CompanyMutation from 'services/graphQL/mutate/CompanyMutation';
import { FetchPolicy } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import ManheimService from 'services/modules/ManheimService';

const useManheimSetting = (canWrite = false) => {
    const context = useContext(UserContext);
    const { userInformation = {} } = context ?? {};

    const [manheimClientId, setManheimClientId] = useState('');
    const [manheimDefaultLot, setManheimDefaultLot] = useState('');
    const [manheimSellerCustomField, setManheimSellerCustomField] = useState('');
    const [manheimLocationStatusCustomField, setManheimLocationStatusCustomField] = useState('');
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importType, setImportType] = useState('vehicles');
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState('');

    const availableLots = (userInformation.lots || []).map((item) => ({
        value: String(item.lotId),
        label: String(item.lotName ?? ''),
    }));

    const {
        data: companySettingsData,
        error: companySettingsError,
        loading: loadingCompanySettings,
    } = useQuery(CompanyQuery.GET_COMPANY_SETTINGS, {
        variables: {
            category: CompanySettingCategory.MANHEIM,
            key: [
                CompanySettingkey.MANHEIM_CLIENT_ID,
                CompanySettingkey.MANHEIM_DEFAULT_LOT,
                CompanySettingkey.MANHEIM_USER_IMPORT,
                CompanySettingkey.MANHEIM_SELLER_CUSTOM_FIELD,
                CompanySettingkey.MANHEIM_LOCATION_STATUS_CUSTOM_FIELD,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: inventoryMetaData,
        loading: inventoryMetaDataLoading,
        error: inventoryMetaDataError,
    } = useQuery(InventoryQuery.GET_INVENTORY_METADATA, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [updateCompanyManheim, {
        loading: isSavingCompanySettings,
    }] = useMutation(CompanyMutation.UPDATE_COMPANY_MANHEIM, {
        onCompleted: () => {
            ModalUtils.successMessage(null, 'Manheim settings saved successfully.');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const loading = loadingCompanySettings || isSavingCompanySettings || inventoryMetaDataLoading;
    const hasLoadedCompanySettings = Array.isArray(companySettingsData?.getCompanySettings)
        && !companySettingsError;
    const manheimUserImport = companySettingsData?.getCompanySettings?.find(
        (setting) => setting.key === CompanySettingkey.MANHEIM_USER_IMPORT,
    )?.value || '';

    const customFields = (inventoryMetaData?.getInventoryMetadata || []).map((item) => ({
        label: [item?.label, item?.property].filter(Boolean).join(' - '),
        value: item?.entityMetadataId || '',
    }));
    const customFieldOptions = customFields.map((option) => ({
        ...option,
        value: String(option.value),
    }));
    const selectedLot = availableLots.find((option) => (
        option.value === String(manheimDefaultLot)
        || option.label === String(manheimDefaultLot)
    ));
    const hasImportResult = Boolean(importResult);
    let inventoryErrors = [];
    let invoiceErrors = [];

    if (importType === 'all') {
        inventoryErrors = [...(importResult?.inventory?.errors || [])];
        invoiceErrors = [...(importResult?.invoice?.errors || [])];
    } else if (importType === 'vehicles') {
        inventoryErrors = [...(importResult?.errors || [])];
    } else if (importType === 'invoices') {
        invoiceErrors = [...(importResult?.errors || [])];
    }
    if (importType === 'all' && importResult?.invoice?.failed) {
        invoiceErrors.push({ vin: '', message: importResult.invoice.message });
    }

    const normalizeErrors = (errors) => (Array.isArray(errors) ? errors : []).map((error) => (
        typeof error === 'string'
            ? { vin: error, message: 'The API did not provide an error message.' }
            : {
                vin: error?.vin || '',
                message: error?.message || 'The API did not provide an error message.',
            }
    ));
    const inventoryErrorRows = normalizeErrors(inventoryErrors);
    const invoiceErrorRows = normalizeErrors(invoiceErrors);
    const hasErrors = inventoryErrorRows.length > 0 || invoiceErrorRows.length > 0;
    let importTitle = 'Vehicles';
    if (importType === 'all') importTitle = 'All';
    else if (importType === 'invoices') importTitle = 'Invoices';

    const buildSummaryRows = (result, includeVendorCount = false) => {
        const rows = [
            { label: 'Created', count: Array.isArray(result?.created) ? result.created.length : 0 },
            { label: 'Updated', count: Array.isArray(result?.updated) ? result.updated.length : 0 },
            { label: 'Duplicate', count: Array.isArray(result?.duplicate) ? result.duplicate.length : 0 },
            { label: 'Sold vehicles', count: Array.isArray(result?.soldVehicles) ? result.soldVehicles.length : 0 },
            { label: 'Errors', count: Array.isArray(result?.errors) ? result.errors.length : 0 },
        ];
        if (includeVendorCount) {
            rows.push({
                label: 'Vendors not found',
                count: Array.isArray(result?.vendorsNotFound) ? result.vendorsNotFound.length : 0,
            });
        }
        return rows;
    };
    const importSummaryRows = buildSummaryRows(importResult, importType === 'invoices');
    const inventorySummaryRows = buildSummaryRows(importResult?.inventory);
    const invoiceSummaryRows = buildSummaryRows(importResult?.invoice, true);

    useEffect(() => {
        if (companySettingsData && companySettingsData?.getCompanySettings) {
            const value = companySettingsData?.getCompanySettings;
            const clientIdSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_CLIENT_ID)?.value || '';
            const defaultLotSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_DEFAULT_LOT)?.value || '';
            const sellerCustomFieldSetting = value?.find((setting) => setting.key === CompanySettingkey.MANHEIM_SELLER_CUSTOM_FIELD)?.value || '';
            const locationStatusCustomFieldSetting = value?.find(
                (setting) => setting.key === CompanySettingkey.MANHEIM_LOCATION_STATUS_CUSTOM_FIELD,
            )?.value || '';

            setManheimClientId(clientIdSetting);
            setManheimDefaultLot(defaultLotSetting);
            setManheimSellerCustomField(sellerCustomFieldSetting);
            setManheimLocationStatusCustomField(locationStatusCustomFieldSetting);
        }
    }, [companySettingsData]);

    useEffect(() => {
        if (companySettingsError) {
            ModalUtils.errorMessage([], companySettingsError?.message);
        }
        if (inventoryMetaDataError) {
            ModalUtils.errorMessage([], inventoryMetaDataError?.message);
        }
    }, [companySettingsError, inventoryMetaDataError]);

    const handleSubmit = () => {
        if (!canWrite || !hasLoadedCompanySettings) return;

        updateCompanyManheim({
            variables: {
                input: {
                    clientId: manheimClientId,
                    lot: manheimDefaultLot,
                    sellerCustomField: manheimSellerCustomField,
                    locationStatusCustomField: manheimLocationStatusCustomField,
                },
            },
        });
    };

    const handleImport = async (type) => {
        if (!canWrite) return;

        setImportType(type);
        setImportDialogOpen(true);
        setIsImporting(true);
        setImportResult(null);
        setImportError('');

        try {
            const manheimService = new ManheimService();
            let response;
            if (type === 'invoices') {
                response = await manheimService.importInvoices(userInformation.companyCode);
            } else if (type === 'all') {
                response = await manheimService.importAll(userInformation.companyCode);
            } else {
                response = await manheimService.importVehicles(userInformation.companyCode);
            }
            setImportResult(response.data);
        } catch (error) {
            const endpointErrors = error.response?.data?.errors;
            const endpointMessage = Array.isArray(endpointErrors)
                ? endpointErrors.map((item) => item?.message).filter(Boolean).join('\n')
                : '';
            setImportError(endpointMessage || error.response?.data?.message || error.message || 'The Manheim import failed.');
        } finally {
            setIsImporting(false);
        }
    };

    const handleImportVehicles = () => handleImport('vehicles');
    const handleImportInvoices = () => handleImport('invoices');
    const handleImportAll = () => handleImport('all');

    const closeImportDialog = () => {
        if (!isImporting) setImportDialogOpen(false);
    };

    return {
        manheimClientId,
        manheimDefaultLot,
        loading,
        hasLoadedCompanySettings,
        availableLots,
        selectedLot,
        manheimUserImport,
        manheimSellerCustomField,
        manheimLocationStatusCustomField,
        customFieldOptions,
        companyCode: userInformation.companyCode,
        importDialogOpen,
        isImporting,
        importType,
        importResult,
        importError,
        importTitle,
        hasImportResult,
        hasErrors,
        inventoryErrorRows,
        invoiceErrorRows,
        importSummaryRows,
        inventorySummaryRows,
        invoiceSummaryRows,
        setManheimClientId,
        setManheimDefaultLot,
        setManheimSellerCustomField,
        setManheimLocationStatusCustomField,
        handleSubmit,
        handleImportVehicles,
        handleImportInvoices,
        handleImportAll,
        closeImportDialog,
    };
};

export default useManheimSetting;
